/**
 * Generic CSV Export Utility
 *
 * A flexible utility for exporting data to CSV with support for:
 * - Custom column headers (display names)
 * - Column selection (visible/hidden columns)
 * - Nested object flattening
 * - Type-safe column definitions
 */

import { downloadCsv, flattenObject, type FlattenedObject } from "./csv-export";

/**
 * Column definition for CSV export
 */
export interface CsvColumn<T = unknown> {
  /** Unique key for the column (property path in data) */
  key: string;
  /** Display header in CSV (defaults to key) */
  header?: string;
  /** Custom formatter function */
  formatter?: (value: unknown, row: T) => string | number | null | undefined;
}

/**
 * Options for CSV export
 */
export interface ExportToCsvOptions<T = unknown> {
  /** Array of data to export */
  data: T[];
  /** Filename (without extension or date) */
  filename: string;
  /** Column definitions (order matters) */
  columns: CsvColumn<T>[];
  /** Whether to flatten nested objects (default: true) */
  flatten?: boolean;
}

/**
 * Escapes a CSV value (handles commas, quotes, newlines)
 */
const escapeCsvValue = (value: unknown): string => {
  if (value == null) {
    return "";
  }
  let str: string;
  if (typeof value === "object") {
    // Avoid [object Object] for objects/arrays
    try {
      str = JSON.stringify(value);
    } catch {
      str = "";
    }
  } else if (typeof value === "string" || typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
    str = String(value);
  } else {
    // symbol or function — not meaningful in CSV output
    str = "";
  }

  // Escape quotes and wrap in quotes if contains comma, quote, or newline
  if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")) {
    return `"${str.replaceAll('"', '""')}"`;
  }

  return str;
};

/**
 * Gets a nested property value from an object using dot notation
 */
const getNestedValue = (obj: Record<string, unknown>, path: string): unknown => {
  return path.split(".").reduce<unknown>((current, key) => {
    if (current && typeof current === "object") {
      return (current as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
};

/**
 * Generic CSV export function with type safety and custom formatting
 *
 * @example
 * ```typescript
 * exportToCsv({
 *   data: invoices,
 *   filename: 'invoice-line-items',
 *   columns: [
 *     { key: 'invoiceId', header: 'Invoice ID' },
 *     { key: 'orderDate', header: 'Order Date', formatter: (val) => formatDate(val) },
 *     { key: 'orderAmount', header: 'Amount', formatter: (val) => formatCurrency(val) },
 *   ],
 * });
 * ```
 */
export const exportToCsv = <T extends Record<string, unknown>>({
  data,
  filename,
  columns,
  flatten = true,
}: ExportToCsvOptions<T>): void => {
  if (data.length === 0) {
    console.warn("[exportToCsv] No data to export");
    return;
  }

  try {
    // Process each row
    const processedRows = data.map((row) => {
      // Flatten if requested
      const flatRow = flatten ? flattenObject(row) : (row as FlattenedObject);

      // Extract values for each column
      return columns.map((col) => {
        // Get the value
        let value: unknown;
        if (col.key in flatRow) {
          value = flatRow[col.key];
        } else {
          // Try nested access for non-flattened data
          value = getNestedValue(row, col.key);
        }

        // Apply formatter if provided
        if (col.formatter) {
          value = col.formatter(value, row);
        }

        return value;
      });
    });

    // Generate CSV headers
    const headers = columns.map((col) => col.header ?? col.key);
    const headerRow = headers.map(escapeCsvValue).join(",");

    // Generate CSV data rows
    const dataRows = processedRows.map((row) => row.map(escapeCsvValue).join(","));

    // Combine into CSV string
    const csv = [headerRow, ...dataRows].join("\n");

    // Download the CSV
    downloadCsv(csv, filename);
  } catch (error) {
    console.error("[exportToCsv] Failed to export CSV:", error);
    throw error;
  }
};
